import { ApiOkResponse, CancelToken } from 'apisauce';
import { AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import LangEnum from '../../lang/LangEnum';
import { paginate } from '../../lib/nest';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { restApi } from '../../services/api';
import snackbarStore from '../../stores/Snackbar.store';
import { recoverValuesFromSearchParams } from '../Crud/useFilter';
import { FetcherOptions, FetcherResponse } from './useList.types';

const defaultFetcherTransformer = (response: ApiOkResponse<any>): FetcherResponse<any> => {
  if (Array.isArray(response.data?.data)) {
    return { ...response.data, tag: 'success' };
  }
  if (Array.isArray(response.data)) {
    return { data: response.data, tag: 'success', count: response.data.length };
  }

  return { tag: 'error', error: 'erro ao decodificar resposta do servidor' };
};

export const fetcher =
  <T, K = any>(endpoint: string, formatOkResponse?: (data: ApiOkResponse<T>) => ApiOkResponse<K>) =>
  async ({
    params,
    page,
    count,
    config,
  }: FetcherOptions & { config?: any }): Promise<FetcherResponse<K>> => {
    const allArgsToFind = {
      ...params,
      ...paginate.limit(count),
      ...paginate.skip(page * count),
      ...paginate.order(params?.order),
    };

    delete allArgsToFind.order;

    const response = await restApi.get<T>(endpoint, allArgsToFind, config);
    if (response.ok) {
      if (formatOkResponse) {
        return defaultFetcherTransformer(formatOkResponse(response));
      }
      return defaultFetcherTransformer(response);
    }
    return { tag: 'error', error: 'Erro ao carregar a lista' };
  };

export const applyChangesToItemsByIds = <ID, T>(
  itemIds: ID[],
  allItems: T[],
  changes: Partial<T>,
) => {
  const list: Record<string, any>[] = allItems;
  if (list.length > 0) {
    const changedItems: any = list.map((item) => {
      const currentItem = item;

      if (itemIds.includes(currentItem.id)) {
        return { ...currentItem, ...changes };
      }

      return currentItem;
    });
    return changedItems;
  }
  return allItems;
};

type ListActionsProps<T, ID> = {
  apiRoute: string;
  screenRoute: string;
  onSearch: (page: number, searchTerm?: any, axiosConfig?: AxiosRequestConfig) => void;
  reload: () => void;
  changeSelecteds: (selecteds: ID[]) => void;
  replaceItems: (selecteds: T[]) => void;
};

export type ListActions<T, ID> = {
  handleRemove: (itemId: ID) => Promise<any>;
  handleMassRemove: (itemIds: ID[], filters?: any) => Promise<any>;
  handleEditSelected: (items: T[], selecteds: ID[]) => void;
  editUrlBuilder: (itemId: ID) => string;
  handleEdit: (itemId: ID) => void;
  loading: boolean;
  handleAdd: () => void;
  toggleAllSelected: (
    isAllItemsSelected: boolean,
    items: T[],
    selecteds: string[],
    fieldRef?: string,
  ) => void;
  applyChangesToItems: (itemIds: ID[], allItems: T[], changes: Partial<T>) => void;
  applyChangesToItem: (itemId: ID, allItems: T[], changes: Partial<T>) => void;
  applyRemovedItems: (itemIds: ID[], allItems: T[]) => void;
  applyRemovedItem: (itemId: ID, allItems: T[]) => void;
};

const useListActions = <T extends { id: ID }, ID>({
  apiRoute,
  screenRoute,
  onSearch,
  reload,
  changeSelecteds,
  replaceItems,
}: ListActionsProps<T, ID>): ListActions<T, ID> => {
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const handleRemove = useCallback(
    async (itemId: ID) => {
      setLoading(true);
      const response = await restApi.delete<any>(`${apiRoute}/${itemId}`);
      setLoading(false);
      if (!response.ok) {
        const messageError = response.data?.code
          ? translate(response.data?.code)
          : response.data?.message;

        handleMessage({
          type: 'error',
          message: messageError ?? translate(LangEnum.USE_LIST_ACTIONS_REMOVE_ERROR),
        });

        return response;
      }
      handleMessage({
        type: 'success',
        message: translate(LangEnum.USE_LIST_ACTIONS_REMOVE_SUCCESS),
      });
      await reload();

      return response;
    },
    [handleMessage, apiRoute, reload],
  );

  const handleMassRemove = useCallback(
    async (itemIds: ID[], filters: any = null) => {
      setLoading(true);
      const response: any = await restApi.delete(apiRoute, undefined, {
        data: { itemIds, promotionSkuFilter: filters },
      });
      setLoading(false);

      if (!response.ok) {
        handleMessage({
          type: 'error',
          message: response.data.message ?? translate(LangEnum.USE_LIST_ACTIONS_MASS_REMOVE_ERROR),
        });
        return;
      }
      handleMessage({
        type: 'success',
        message: translate(LangEnum.USE_LIST_ACTIONS_MASS_REMOVE_SUCCESS),
      });

      changeSelecteds([]);

      await reload();
    },
    [apiRoute, handleMessage, changeSelecteds, reload],
  );

  const editUrlBuilder = useCallback((itemId: ID) => `${screenRoute}/${itemId}`, [screenRoute]);

  const handleEdit = useCallback(
    (itemId: ID) =>
      history.push(editUrlBuilder(itemId), {
        originUrl: location.pathname,
        filters: location.search,
      }),
    [history, editUrlBuilder, location.pathname, location.search],
  );

  const handleAdd = useCallback(
    () =>
      history.push(`${screenRoute}/new`, {
        originUrl: location.pathname,
        filters: location.search,
      }),
    [history, location.pathname, location.search, screenRoute],
  );

  const handleEditSelected = useCallback(
    (items: T[], selecteds: ID[]) => {
      const selectedItem: any = items.find((item: any) => item.id === selecteds[0]);
      if (selectedItem) {
        handleEdit(selectedItem.id);
      }
    },
    [handleEdit],
  );

  const toggleAllSelected = useCallback(
    (isAllItemsSelected: boolean, items: T[], selecteds: string[], fieldRef?: string) => {
      let newSelecteds: ID[] = [];
      if (!isAllItemsSelected) {
        newSelecteds = items.map((item: any) => item[fieldRef || 'id']);
      }

      if (selecteds.length !== 0) {
        return changeSelecteds([]);
      }

      changeSelecteds(newSelecteds);
    },
    [changeSelecteds],
  );

  const applyChangesToItems = (itemIds: ID[], allItems: T[], changes: Partial<T>) => {
    const changedItems = applyChangesToItemsByIds(itemIds, allItems, changes);
    replaceItems(changedItems);
  };

  const applyChangesToItem = (itemId: ID, allItems: T[], changes: Partial<T>) => {
    applyChangesToItems([itemId], allItems, changes);
  };

  const applyRemovedItems = (itemIds: ID[], allItems: T[]) => {
    if (allItems && allItems.length > 0) {
      const remainingItems = allItems.filter((item) => !itemIds.includes(item.id));
      replaceItems(remainingItems);
    }
  };

  const applyRemovedItem = (itemId: ID, allItems: T[]) => {
    applyRemovedItems([itemId], allItems);
  };

  useEffect(() => {
    const source = CancelToken.source();
    onSearch(1, recoverValuesFromSearchParams(location.search), { cancelToken: source.token });
    return () => {
      source.cancel();
    };
  }, [location.search, onSearch]);

  return {
    handleRemove,
    loading,
    handleMassRemove,
    editUrlBuilder,
    handleEditSelected,
    handleEdit,
    handleAdd,
    toggleAllSelected,
    applyChangesToItems,
    applyChangesToItem,
    applyRemovedItems,
    applyRemovedItem,
  };
};

export default useListActions;
